import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import {
  ArrowLeft, Plus, Trash2, GripVertical, CircleDot, Type, Star, Calendar,
  ListOrdered, Table, Paperclip, Gauge, LayoutList, ChevronDown, ChevronUp,
  Settings, Eye, X, Upload, ClipboardList, User, Briefcase, EyeOff
} from 'lucide-react';

const QUESTION_TYPES = [
  { type: 'choice', label: 'Choice', icon: CircleDot, description: 'Single or multiple choice' },
  { type: 'text', label: 'Text', icon: Type, description: 'Short or long text answer' },
  { type: 'rating', label: 'Rating', icon: Star, description: 'Star/number rating' },
  { type: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { type: 'ranking', label: 'Ranking', icon: ListOrdered, description: 'Rank items in order' },
  { type: 'likert', label: 'Likert Scale', icon: Table, description: 'Matrix rating scale' },
  { type: 'file', label: 'File Upload', icon: Paperclip, description: 'File attachment' },
  { type: 'nps', label: 'NPS', icon: Gauge, description: 'Net Promoter Score 0-10' },
  { type: 'user_select', label: 'User Select', icon: User, description: 'Select users to evaluate' },
  { type: 'project_select', label: 'Project Select', icon: Briefcase, description: 'Select projects' },
  { type: 'section', label: 'Section', icon: LayoutList, description: 'Section header' },
];

const RATING_SYMBOLS = [
  { value: 'star', label: 'Stars' },
  { value: 'flag', label: 'Flags' },
  { value: 'number', label: 'Numbers' },
  { value: 'heart', label: 'Hearts' },
  { value: 'ribbon', label: 'Ribbons' },
  { value: 'thumb', label: 'Thumbs' },
  { value: 'smile', label: 'Smileys' },
];

const SurveyBuilder = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingSurveySettings, setEditingSurveySettings] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectOptions, setSelectOptions] = useState({ users: [], projects: [] });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  useEffect(() => {
    fetchSurvey();
    fetchSelectOptions();
  }, [surveyId]);

  const fetchSelectOptions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/select-options/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectOptions(response.data);
    } catch (error) {
      console.error('Error fetching select options:', error);
    }
  };

  const showToastNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const fetchSurvey = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSurvey(response.data);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching survey:', error);
      showToastNotification('Error loading survey');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setRemoveExistingImage(false);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (survey?.image_url) setRemoveExistingImage(true);
  };

  const handleUpdateSurvey = async (updates, newImageFile = null, shouldRemoveImage = false) => {
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      Object.keys(updates).forEach(key => {
        if (updates[key] !== null && updates[key] !== undefined) {
          formData.append(key, typeof updates[key] === 'boolean' ? (updates[key] ? 'true' : 'false') : updates[key]);
        }
      });
      if (newImageFile) formData.append('image', newImageFile);
      else if (shouldRemoveImage) formData.append('remove_image', 'true');

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setSurvey(response.data);
      setImageFile(null);
      setImagePreview(null);
      setRemoveExistingImage(false);
      showToastNotification('Survey updated');
    } catch (error) {
      console.error('Error updating survey:', error);
      showToastNotification('Error updating survey');
    }
  };

  const handleAddQuestion = async (type) => {
    try {
      const token = localStorage.getItem('access_token');
      const newQuestion = {
        question_type: type,
        question_text: type === 'section' ? 'New Section' : 'New Question',
        description: '',
        is_required: false,
        allow_multiple: false,
        rating_levels: 5,
        rating_symbol: 'star',
        text_multiline: false,
        nps_min_label: 'Not at all likely',
        nps_max_label: 'Extremely likely',
        options: type === 'choice' || type === 'ranking' ? [{ text: 'Option 1', order: 0 }] :
                 type === 'likert' ? [
                   { text: 'Strongly Disagree', order: 0 },
                   { text: 'Disagree', order: 1 },
                   { text: 'Neutral', order: 2 },
                   { text: 'Agree', order: 3 },
                   { text: 'Strongly Agree', order: 4 }
                 ] : [],
        statements: type === 'likert' ? [{ text: 'Statement 1', order: 0 }] : []
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/questions/`,
        newQuestion,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setQuestions([...questions, response.data]);
      setExpandedQuestion(response.data.id);
      setShowAddPanel(false);
      showToastNotification('Question added');
    } catch (error) {
      console.error('Error adding question:', error);
      showToastNotification('Error adding question');
    }
  };

  const handleLocalUpdate = (questionId, updates) => {
    setEditingQuestion(prev => {
      if (!prev || prev.id !== questionId) {
        const question = questions.find(q => q.id === questionId);
        if (question) return { ...question, ...updates };
        return prev;
      }
      return { ...prev, ...updates };
    });
  };

  const handleSaveQuestion = async (questionId, dataOverride = null) => {
    const dataToSave = dataOverride || editingQuestion;
    if (!dataToSave || dataToSave.id !== questionId) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/questions/${questionId}/`,
        dataToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuestions(prev => prev.map(q => q.id === questionId ? response.data : q));
      setEditingQuestion(response.data);
    } catch (error) {
      console.error('Error updating question:', error);
      showToastNotification('Error saving question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/questions/${questionId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuestions(questions.filter(q => q.id !== questionId));
      showToastNotification('Question deleted');
    } catch (error) {
      console.error('Error deleting question:', error);
      showToastNotification('Error deleting question');
    }
  };

  const getQuestionIcon = (type) => {
    const questionType = QUESTION_TYPES.find(t => t.type === type);
    return questionType ? questionType.icon : Type;
  };

  const renderQuestionEditor = (question) => {
    const Icon = getQuestionIcon(question.question_type);
    const isExpanded = expandedQuestion === question.id;
    const q = (isExpanded && editingQuestion?.id === question.id) ? editingQuestion : question;

    const handleExpand = () => {
      if (isExpanded) {
        if (editingQuestion?.id === question.id) handleSaveQuestion(question.id);
        setExpandedQuestion(null);
        setEditingQuestion(null);
      } else {
        setExpandedQuestion(question.id);
        setEditingQuestion({ ...question });
      }
    };

    return (
      <div key={question.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${isExpanded ? 'border-orange-300' : 'border-slate-100'}`}>
        <div className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors" onClick={handleExpand}>
          <GripVertical size={16} className="text-slate-300 flex-shrink-0" />
          <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Icon size={14} className="text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              {QUESTION_TYPES.find(t => t.type === question.question_type)?.label}
            </span>
            <p className="text-sm font-bold text-slate-900 truncate">{q.question_text || 'Untitled question'}</p>
          </div>
          <div className="flex items-center gap-2">
            {q.is_required && (
              <span className="px-2 py-0.5 text-[9px] font-bold bg-red-100 text-red-600 rounded-md uppercase">Required</span>
            )}
            {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </div>
        </div>

        {isExpanded && (
          <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-4">
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                {q.question_type === 'section' ? 'Section Title' : 'Question'}
              </label>
              <input
                type="text"
                value={q.question_text}
                onChange={e => handleLocalUpdate(question.id, { question_text: e.target.value })}
                onBlur={() => handleSaveQuestion(question.id)}
                placeholder="Enter question text"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Description</label>
              <textarea
                value={q.description || ''}
                onChange={e => handleLocalUpdate(question.id, { description: e.target.value })}
                onBlur={() => handleSaveQuestion(question.id)}
                placeholder="Add a description"
                rows={2}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
              />
            </div>

            {/* Choice options */}
            {q.question_type === 'choice' && (
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={q.allow_multiple}
                    onChange={e => {
                      const updatedData = { ...q, allow_multiple: e.target.checked };
                      handleLocalUpdate(question.id, { allow_multiple: e.target.checked });
                      setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                    }}
                    className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-bold text-slate-700">Allow multiple selections</span>
                </label>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Options</label>
                  <div className="space-y-2">
                    {(q.options || []).map((option, idx) => (
                      <div key={option.id || `new-${idx}`} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={option.text}
                          onChange={e => {
                            const newOptions = [...q.options];
                            newOptions[idx] = { ...option, text: e.target.value };
                            handleLocalUpdate(question.id, { options: newOptions });
                          }}
                          onBlur={() => handleSaveQuestion(question.id)}
                          placeholder={`Option ${idx + 1}`}
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <button
                          onClick={() => {
                            const newOptions = q.options.filter((_, i) => i !== idx);
                            const updatedData = { ...q, options: newOptions };
                            handleLocalUpdate(question.id, { options: newOptions });
                            setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                          }}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newOptions = [...(q.options || []), { text: '', order: q.options?.length || 0 }];
                        handleLocalUpdate(question.id, { options: newOptions });
                      }}
                      className="flex items-center gap-1.5 text-xs font-bold text-orange-500 hover:text-orange-600"
                    >
                      <Plus size={14} /> Add Option
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Text options */}
            {q.question_type === 'text' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={q.text_multiline}
                  onChange={e => {
                    const updatedData = { ...q, text_multiline: e.target.checked };
                    handleLocalUpdate(question.id, { text_multiline: e.target.checked });
                    setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                  }}
                  className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm font-bold text-slate-700">Long answer (multiline)</span>
              </label>
            )}

            {/* Rating options */}
            {q.question_type === 'rating' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Levels</label>
                  <select
                    value={q.rating_levels}
                    onChange={e => {
                      const updatedData = { ...q, rating_levels: parseInt(e.target.value) };
                      handleLocalUpdate(question.id, { rating_levels: parseInt(e.target.value) });
                      setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    {[3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Symbol</label>
                  <select
                    value={q.rating_symbol}
                    onChange={e => {
                      const updatedData = { ...q, rating_symbol: e.target.value };
                      handleLocalUpdate(question.id, { rating_symbol: e.target.value });
                      setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    {RATING_SYMBOLS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* NPS options */}
            {q.question_type === 'nps' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Min Label (0)</label>
                  <input
                    type="text"
                    value={q.nps_min_label}
                    onChange={e => handleLocalUpdate(question.id, { nps_min_label: e.target.value })}
                    onBlur={() => handleSaveQuestion(question.id)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Max Label (10)</label>
                  <input
                    type="text"
                    value={q.nps_max_label}
                    onChange={e => handleLocalUpdate(question.id, { nps_max_label: e.target.value })}
                    onBlur={() => handleSaveQuestion(question.id)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            )}

            {/* Ranking options */}
            {q.question_type === 'ranking' && (
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Items to Rank</label>
                <div className="space-y-2">
                  {(q.options || []).map((option, idx) => (
                    <div key={option.id || `new-${idx}`} className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{idx + 1}</span>
                      <input
                        type="text"
                        value={option.text}
                        onChange={e => {
                          const newOptions = [...q.options];
                          newOptions[idx] = { ...option, text: e.target.value };
                          handleLocalUpdate(question.id, { options: newOptions });
                        }}
                        onBlur={() => handleSaveQuestion(question.id)}
                        placeholder={`Item ${idx + 1}`}
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                      <button
                        onClick={() => {
                          const newOptions = q.options.filter((_, i) => i !== idx);
                          const updatedData = { ...q, options: newOptions };
                          handleLocalUpdate(question.id, { options: newOptions });
                          setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                        }}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newOptions = [...(q.options || []), { text: '', order: q.options?.length || 0 }];
                      handleLocalUpdate(question.id, { options: newOptions });
                    }}
                    className="flex items-center gap-1.5 text-xs font-bold text-orange-500 hover:text-orange-600"
                  >
                    <Plus size={14} /> Add Item
                  </button>
                </div>
              </div>
            )}

            {/* Likert options */}
            {q.question_type === 'likert' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Scale Options (Columns)</label>
                  <div className="flex flex-wrap gap-2">
                    {(q.options || []).map((option, idx) => (
                      <div key={option.id || `new-${idx}`} className="flex items-center gap-1 bg-slate-50 rounded-lg p-1">
                        <input
                          type="text"
                          value={option.text}
                          onChange={e => {
                            const newOptions = [...q.options];
                            newOptions[idx] = { ...option, text: e.target.value };
                            handleLocalUpdate(question.id, { options: newOptions });
                          }}
                          onBlur={() => handleSaveQuestion(question.id)}
                          placeholder={`Col ${idx + 1}`}
                          className="w-24 px-2 py-1 border border-slate-200 rounded text-xs font-medium text-slate-900 placeholder-slate-400 focus:ring-1 focus:ring-orange-500"
                        />
                        <button
                          onClick={() => {
                            const newOptions = q.options.filter((_, i) => i !== idx);
                            const updatedData = { ...q, options: newOptions };
                            handleLocalUpdate(question.id, { options: newOptions });
                            setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                          }}
                          className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newOptions = [...(q.options || []), { text: '', order: q.options?.length || 0 }];
                        handleLocalUpdate(question.id, { options: newOptions });
                      }}
                      className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Statements (Rows)</label>
                  <div className="space-y-2">
                    {(q.statements || []).map((statement, idx) => (
                      <div key={statement.id || `new-${idx}`} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={statement.text}
                          onChange={e => {
                            const newStatements = [...q.statements];
                            newStatements[idx] = { ...statement, text: e.target.value };
                            handleLocalUpdate(question.id, { statements: newStatements });
                          }}
                          onBlur={() => handleSaveQuestion(question.id)}
                          placeholder={`Statement ${idx + 1}`}
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <button
                          onClick={() => {
                            const newStatements = q.statements.filter((_, i) => i !== idx);
                            const updatedData = { ...q, statements: newStatements };
                            handleLocalUpdate(question.id, { statements: newStatements });
                            setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                          }}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newStatements = [...(q.statements || []), { text: '', order: q.statements?.length || 0 }];
                        handleLocalUpdate(question.id, { statements: newStatements });
                      }}
                      className="flex items-center gap-1.5 text-xs font-bold text-orange-500 hover:text-orange-600"
                    >
                      <Plus size={14} /> Add Statement
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
              {q.question_type !== 'section' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={q.is_required}
                    onChange={e => {
                      const updatedData = { ...q, is_required: e.target.checked };
                      handleLocalUpdate(question.id, { is_required: e.target.checked });
                      setTimeout(() => handleSaveQuestion(question.id, updatedData), 100);
                    }}
                    className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-bold text-slate-700">Required</span>
                </label>
              )}
              <button
                onClick={() => handleDeleteQuestion(question.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading survey...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm border border-slate-100">
          <p className="text-sm font-bold text-slate-900">Survey not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/surveys')} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-sm font-black text-slate-900">{survey.title}</h1>
              <span className="text-[10px] text-slate-400 font-medium">{questions.length} questions</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`h-9 px-3 rounded-xl flex items-center gap-1.5 font-bold text-xs transition-all ${
                showPreview
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button
              onClick={() => setEditingSurveySettings(true)}
              className="h-9 px-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 flex items-center gap-1.5 font-bold text-xs transition-colors"
            >
              <Settings size={14} /> Settings
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className={`grid gap-6 ${showPreview ? 'grid-cols-2' : 'grid-cols-1 max-w-3xl mx-auto'}`}>
          {/* Questions List */}
          <div className="space-y-3">
            {questions.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <ClipboardList className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-sm font-bold text-slate-900 mb-1">No questions yet</p>
                <p className="text-xs text-slate-400">Add your first question to get started</p>
              </div>
            ) : (
              questions.map(question => renderQuestionEditor(question))
            )}

            {/* Add Question Button */}
            <button
              onClick={() => setShowAddPanel(true)}
              className="w-full h-12 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-slate-200 rounded-2xl text-sm font-bold text-slate-500 hover:border-orange-300 hover:text-orange-500 hover:bg-orange-50/50 transition-all"
            >
              <Plus size={18} /> Add Question
            </button>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden sticky top-20 h-fit max-h-[calc(100vh-120px)] overflow-y-auto">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Eye className="h-4 w-4 text-violet-600" />
                  </div>
                  <h2 className="text-sm font-black text-slate-900 uppercase">Preview</h2>
                </div>
                <button onClick={() => setShowPreview(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                  <X size={16} />
                </button>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-black text-slate-900 mb-1">{survey.title}</h3>
                {survey.description && <p className="text-sm text-slate-500 mb-4">{survey.description}</p>}

                {questions.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardList className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">Add questions to see preview</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {questions.map(question => {
                      const q = (editingQuestion?.id === question.id) ? editingQuestion : question;
                      if (q.question_type === 'section') {
                        return (
                          <div key={q.id} className="pt-4 border-t border-slate-200">
                            <h4 className="text-sm font-black text-slate-900">{q.question_text}</h4>
                            {q.description && <p className="text-xs text-slate-500 mt-1">{q.description}</p>}
                          </div>
                        );
                      }
                      return (
                        <div key={q.id} className="p-3 bg-slate-50 rounded-xl">
                          <p className="text-sm font-bold text-slate-900 mb-2">
                            {q.question_text}
                            {q.is_required && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          {q.description && <p className="text-xs text-slate-500 mb-2">{q.description}</p>}

                          {/* Render preview based on type */}
                          {q.question_type === 'choice' && (
                            <div className="space-y-1.5">
                              {(q.options || []).map((opt, idx) => (
                                <label key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                                  <input type={q.allow_multiple ? 'checkbox' : 'radio'} disabled className="text-orange-500" />
                                  {opt.text || `Option ${idx + 1}`}
                                </label>
                              ))}
                            </div>
                          )}
                          {q.question_type === 'text' && (
                            q.text_multiline
                              ? <textarea disabled placeholder="Your answer..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" rows={3} />
                              : <input type="text" disabled placeholder="Your answer..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" />
                          )}
                          {q.question_type === 'rating' && (
                            <div className="flex gap-2">
                              {[...Array(q.rating_levels || 5)].map((_, idx) => (
                                <div key={idx} className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-500">
                                  {q.rating_symbol === 'number' ? idx + 1 : '★'}
                                </div>
                              ))}
                            </div>
                          )}
                          {q.question_type === 'date' && (
                            <input type="date" disabled className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" />
                          )}
                          {q.question_type === 'nps' && (
                            <div>
                              <div className="flex gap-1 mb-1">
                                {[...Array(11)].map((_, idx) => (
                                  <div key={idx} className="w-7 h-7 rounded bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">{idx}</div>
                                ))}
                              </div>
                              <div className="flex justify-between text-[10px] text-slate-400">
                                <span>{q.nps_min_label}</span>
                                <span>{q.nps_max_label}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Question Panel */}
      {showAddPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddPanel(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-orange-500" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase">Add Question</h2>
              </div>
              <button onClick={() => setShowAddPanel(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
              {QUESTION_TYPES.map(qt => (
                <button
                  key={qt.type}
                  onClick={() => handleAddQuestion(qt.type)}
                  className="p-3 text-left bg-slate-50 hover:bg-orange-50 hover:border-orange-200 border border-slate-100 rounded-xl transition-all"
                >
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center mb-2">
                    <qt.icon size={14} className="text-orange-500" />
                  </div>
                  <p className="text-sm font-bold text-slate-900">{qt.label}</p>
                  <p className="text-[10px] text-slate-500">{qt.description}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Survey Settings Modal */}
      {editingSurveySettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setEditingSurveySettings(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-slate-600" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase">Survey Settings</h2>
              </div>
              <button onClick={() => setEditingSurveySettings(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Title</label>
                <input
                  type="text"
                  value={survey.title}
                  onChange={e => setSurvey({ ...survey, title: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Description</label>
                <textarea
                  value={survey.description || ''}
                  onChange={e => setSurvey({ ...survey, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Deadline</label>
                <input
                  type="datetime-local"
                  value={survey.deadline ? survey.deadline.slice(0, 16) : ''}
                  onChange={e => setSurvey({ ...survey, deadline: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Cover Image</label>
                {(imagePreview || (survey.image_url && !removeExistingImage)) ? (
                  <div className="relative rounded-xl overflow-hidden border border-slate-200">
                    <img src={imagePreview || survey.image_url} alt="Cover" className="w-full h-32 object-cover" />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-lg text-white hover:bg-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-orange-300 hover:bg-orange-50/50 transition-colors">
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                    <Upload className="h-6 w-6 text-slate-400 mb-2" />
                    <span className="text-xs font-bold text-slate-600">Click to upload</span>
                    <span className="text-[10px] text-slate-400">PNG, JPG up to 5MB</span>
                  </label>
                )}
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={survey.is_active}
                    onChange={e => setSurvey({ ...survey, is_active: e.target.checked })}
                    className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-bold text-slate-700">Active (visible to users)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={survey.is_anonymous}
                    onChange={e => setSurvey({ ...survey, is_anonymous: e.target.checked })}
                    className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-bold text-slate-700">Anonymous responses</span>
                </label>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
              <button
                onClick={() => { setEditingSurveySettings(false); setImageFile(null); setImagePreview(null); setRemoveExistingImage(false); }}
                className="h-9 px-4 text-slate-600 rounded-xl hover:bg-slate-200 font-bold text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleUpdateSurvey({
                    title: survey.title,
                    description: survey.description,
                    deadline: survey.deadline,
                    is_active: survey.is_active,
                    is_anonymous: survey.is_anonymous
                  }, imageFile, removeExistingImage);
                  setEditingSurveySettings(false);
                }}
                className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-bold text-xs uppercase tracking-wide transition-all shadow-lg shadow-orange-500/20"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default SurveyBuilder;
